import {useRef, useState} from 'react'
import Overlay from 'react-bootstrap/Overlay'
import Popover from 'react-bootstrap/Popover'
import {DayPicker} from 'react-day-picker'
import 'react-day-picker/style.css'
import '../common/weekCalendar.css'
import {addDays, endOfWeek, format, isSameMonth, isSameYear, startOfWeek} from 'date-fns'
import {LocaleArrow} from '../common/LocaleArrow'
import {useStore} from '../../store'
import {WeekDays} from '../../helpers/generals'

/**
 * "Oct 18 – 24, 2026", "Oct 29 – Nov 4, 2026", "Dec 29, 2026 – Jan 4, 2027":
 * only what differs between the two ends is repeated.
 */
export const weekLabel = (start: Date, end: Date, locale: any) => {
  const f = (d: Date, pattern: string) => format(d, pattern, {locale})
  if (!isSameYear(start, end)) return `${f(start, 'MMM d, yyyy')} – ${f(end, 'MMM d, yyyy')}`
  if (!isSameMonth(start, end)) return `${f(start, 'MMM d')} – ${f(end, 'MMM d, yyyy')}`
  return `${f(start, 'MMM d')} – ${f(end, 'd, yyyy')}`
}

/**
 * Put keyboard focus on the calendar's current day once its popover has been
 * positioned. DayPicker's own autoFocus fires on mount, while the popover is
 * still unplaced at the end of <body>, and the browser scrolled the whole page
 * to the bottom to show it.
 */
export const focusCalendarDay = (popoverId: string) => {
  const root = document.getElementById(popoverId)
  const day = root?.querySelector<HTMLButtonElement>('.rdp-day_button[tabindex="0"]')
  day?.focus({preventScroll: true})
}

interface DateNavigatorProps {
  // What one step of the arrows and one pick in the calendar means.
  unit: 'week' | 'day'
  selectedDate: Date
  weekStartOn: WeekDays
  onChange(value: Date, key: 'selectedDate'): void
}

// The years the month/year selects offer: ten back, five ahead, and always
// the shown one.
const yearRange = (shown: Date) => {
  const now = new Date().getFullYear()
  return {
    startMonth: new Date(Math.min(now - 10, shown.getFullYear()), 0),
    endMonth: new Date(Math.max(now + 5, shown.getFullYear()), 11),
  }
}

/**
 * The board's date navigator, shared by the week and day views: prev/next
 * and a button that opens a month calendar.
 *
 * In week mode the unit of choice is the WEEK — hovering previews the whole
 * row and a click anywhere in it selects that week. In day mode it is a plain
 * day pick, with the shown day's week faintly banded for orientation.
 *
 * Replaces inline react-datepickers with month/year <select>s. The arrows keep
 * their drag-over paging (drag a shift onto them to move along).
 */
const DateNavigator = ({unit, selectedDate, weekStartOn, onChange}: DateNavigatorProps) => {
  const {locale} = useStore()
  const weekStart = startOfWeek(selectedDate, {weekStartsOn: weekStartOn})
  const weekEnd = endOfWeek(selectedDate, {weekStartsOn: weekStartOn})

  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState<Date>(selectedDate)
  const [hovered, setHovered] = useState<Date | null>(null)
  const target = useRef<HTMLButtonElement>(null)

  const toggle = () => {
    if (!open) {
      // Open on the shown date's month, not wherever the calendar was left.
      setMonth(unit === 'week' ? weekStart : selectedDate)
      setHovered(null)
    }
    setOpen(!open)
  }

  const pick = (day: Date) => {
    onChange(day, 'selectedDate')
    setOpen(false)
  }

  const handlePrev = () =>
    onChange(unit === 'week' ? addDays(weekStart, -1) : addDays(selectedDate, -1), 'selectedDate')
  const handleNext = () =>
    onChange(unit === 'week' ? addDays(weekEnd, 1) : addDays(selectedDate, 1), 'selectedDate')

  const label =
    unit === 'week'
      ? weekLabel(weekStart, weekEnd, locale)
      : format(selectedDate, 'EEE, MMM d, yyyy', {locale})

  const hoverWeek = unit === 'week' &&
    hovered && {
      from: startOfWeek(hovered, {weekStartsOn: weekStartOn}),
      to: endOfWeek(hovered, {weekStartsOn: weekStartOn}),
    }

  return (
    <div className='d-flex align-items-center gap-1'>
      <LocaleArrow type='prev' onClick={handlePrev} />
      <button
        ref={target}
        type='button'
        className='btn btn-sm btn-light btn-active-light-primary fw-bold text-gray-800 text-nowrap'
        onClick={toggle}
        aria-haspopup='dialog'
        aria-expanded={open}
      >
        <i className='bi bi-calendar3 fs-6 text-gray-500'></i>
        {label}
      </button>
      <LocaleArrow type='next' onClick={handleNext} />

      <Overlay
        target={target.current}
        show={open}
        placement='bottom'
        // Fixed, like the toolbar it hangs off (fixed on mobile): an absolute
        // popover was placed as if it scrolled with the page.
        popperConfig={{strategy: 'fixed'}}
        onEntered={() => focusCalendarDay('date-navigator')}
        rootClose
        onHide={() => setOpen(false)}
      >
        <Popover id='date-navigator' className='shadow-sm' style={{maxWidth: 'none'}}>
          <Popover.Body className='p-3'>
            <DayPicker
              className='fds-calendar'
              month={month}
              onMonthChange={setMonth}
              weekStartsOn={weekStartOn}
              // Month and year selects, to jump far without paging.
              captionLayout='dropdown'
              {...yearRange(selectedDate)}
              showOutsideDays
              fixedWeeks
              showWeekNumber
              onDayClick={pick}
              onDayMouseEnter={(day) => setHovered(day)}
              onDayMouseLeave={() => setHovered(null)}
              // Keyboard users get the same band preview as the mouse.
              onDayFocus={(day) => setHovered(day)}
              modifiers={{
                weekSelected: unit === 'week' ? {from: weekStart, to: weekEnd} : [],
                weekContext: unit === 'day' ? {from: weekStart, to: weekEnd} : [],
                daySelected: unit === 'day' ? selectedDate : [],
                weekHover: hoverWeek || [],
                weekFirst: {dayOfWeek: [weekStartOn]},
                weekLast: {dayOfWeek: [(weekStartOn + 6) % 7]},
              }}
              modifiersClassNames={{
                weekSelected: 'fds-week-selected',
                weekContext: 'fds-week-context',
                daySelected: 'fds-day-selected',
                weekHover: 'fds-week-hover',
                weekFirst: 'fds-week-first',
                weekLast: 'fds-week-last',
              }}
            />
          </Popover.Body>
        </Popover>
      </Overlay>
    </div>
  )
}

export {DateNavigator}
